import React from 'react'
import ReactDOM from 'react-dom'
import {Provider} from 'react-redux'
import * as Sentry from '@sentry/browser'
import {createStore, applyMiddleware, compose} from 'redux'
import thunkMiddleware from 'redux-thunk'
import {Switch, Redirect} from 'react-router-dom'
import {withRouter} from 'react-router'
import {ConnectedRouter, routerMiddleware} from 'connected-react-router'
import HttpsRedirect from 'react-https-redirect'
import Authenticated from './containers/Authenticated'
import RouteWithLayout from './components/RouteWithLayout'
import Loadable from './components/Loadable'
import ScrollToTop from './components/ScrollToTop'
import history from './utils/history'
import rootReducer from './store/reducers'
import 'antd/dist/antd.css'
import './index.css'
import './mediaQueries.css'
import 'moment/locale/es'
import * as serviceWorker from './serviceWorker'
import {GoogleOAuthProvider} from "@react-oauth/google";

const {REACT_APP_AUTH_CLIENT_ID: oauthClientId} = process.env;

const AsyncNavbar = Loadable({
  loader: () => import('./containers/Navbar')
})

const AsyncNavbarBackOffice = Loadable({
  loader: () => import('./containers/NavbarBackOffice')
})

const AsyncLogin = Loadable({
  loader: () => import('./containers/Login')
})

const AsyncMySolicitudes = Loadable({
  loader: () => import('./containers/MySolicitudes')
})

const AsyncBackOfficeSteps = Loadable({
  loader: () => import('./containers/BackOfficeSteps')
})

const AsyncSimulationSteps = Loadable({
  loader: () => import('./containers/SimulationSteps')
})

const AsyncSignDocuments = Loadable({
  loader: () => import('./containers/SignDocuments')
})

const AsyncCompletedProcess = Loadable({
  loader: () => import('./components/CompletedProcess')
})

const AsyncExpiredSession = Loadable({
  loader: () => import('./components/ExpiredSession')
})

const AsyncNotFound = Loadable({
  loader: () => import('./components/NotFound')
})


const historyMiddleware = routerMiddleware(history)
let middleware = [historyMiddleware, thunkMiddleware]
let enhancers = null

if (process.env.NODE_ENV === 'production') {
  enhancers = applyMiddleware(...middleware)
  if (typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__ === 'object') {
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__._renderers = {}
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__.inject = function () { }
  }

  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    environment: process.env.SENTRY_ENVIRONMENT
  })

} else {
  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
  middleware = [...middleware]
  enhancers = composeEnhancers(applyMiddleware(...middleware))
}

export const store = createStore(
  rootReducer(history),
  enhancers
)

if (module.hot) {
  module.hot.accept('./store/reducers', () => {
    const nextRootReducer = require('./store/reducers').default
    store.replaceReducer(nextRootReducer)
  })
}

const RedirectWithQuery = withRouter((props) => {
  return <Redirect {...props} to={props.to + props.location.search}/>
})

ReactDOM.render(
  <GoogleOAuthProvider clientId={oauthClientId}>
    <HttpsRedirect>
      <Provider store={store}>
        <ConnectedRouter history={history}>
          <ScrollToTop>
            <Switch>
              <RouteWithLayout
                exact
                layout={AsyncNavbarBackOffice}
                path='/backoffice'
                component={Authenticated(AsyncBackOfficeSteps, ['CCLAUser'], false)}
              />
              <RouteWithLayout
                path='/simulacion'
                layout={AsyncNavbar}
                component={Authenticated(AsyncSimulationSteps, ['User'], true)}
              />
              <RouteWithLayout
                path='/firmar-documentos'
                layout={AsyncNavbar}
                component={Authenticated(AsyncSignDocuments, ['User'], true)}
              />
              <RouteWithLayout
                path='/mis-solicitudes'
                layout={AsyncNavbar}
                component={Authenticated(AsyncMySolicitudes, ['User'], true)}/>
              <RedirectWithQuery exact from='/' to='/simulacion'/>
              <RouteWithLayout
                exact
                path='/login'
                layout={AsyncNavbar}
                component={AsyncLogin}/>
              <RouteWithLayout
                exact
                path='/proceso-completado'
                layout={AsyncNavbar}
                component={AsyncCompletedProcess}
              />
              <RouteWithLayout
                exact
                path='/sesion-expirada'
                layout={AsyncNavbar}
                component={AsyncExpiredSession}
              />
              <RouteWithLayout layout={AsyncNavbar} component={AsyncNotFound}/>
            </Switch>
          </ScrollToTop>
        </ConnectedRouter>
      </Provider>
    </HttpsRedirect>
  </GoogleOAuthProvider>,
  document.getElementById('root'))

serviceWorker.unregister()
